import React, { useEffect, useState } from "react";
import { api, formatMoney, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus } from "lucide-react";

const PRESETS = [25, 50, 100, 250, 500, 1000];
const CURRENCIES = ["usd", "eur", "gbp", "inr", "aed", "sgd"];

export default function Wallet() {
  const [wallet, setWallet] = useState(null);
  const [currency, setCurrency] = useState("usd");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/wallet").then(({ data }) => setWallet(data)).catch(() => {});
  }, []);

  const topUp = async () => {
    const parsed = parseFloat(amount);
    if (!parsed || parsed <= 0) { toast.error("Enter a valid amount"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        top_up_amount: parsed,
        top_up_currency: currency,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setLoading(false);
    }
  };

  const balances = wallet?.balances || {};
  const activeBalance = balances[currency] ?? 0;

  return (
    <main data-testid="wallet-page" className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
      <div className="text-xs uppercase tracking-[0.3em] text-gold">Vault</div>
      <h1 className="font-display text-5xl tracking-tighter mt-3">Wallet</h1>

      <div className="mt-12 lux-card p-12">
        <div className="grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 md:col-span-7">
            <div className="text-xs uppercase tracking-[0.3em] text-muted-fg">Balance · {currency.toUpperCase()}</div>
            <div data-testid="wallet-balance" className="font-display text-7xl lg:text-8xl gold-text mt-4 tracking-tighter font-light">
              {formatMoney(activeBalance, currency)}
            </div>
          </div>
          <div className="col-span-12 md:col-span-5 md:border-l hairline md:pl-8">
            <div className="text-xs uppercase tracking-widest text-muted-fg mb-3">All currencies</div>
            <div className="grid grid-cols-2 gap-2">
              {CURRENCIES.map((c) => (
                <button
                  key={c}
                  data-testid={`currency-tab-${c}`}
                  type="button"
                  onClick={() => setCurrency(c)}
                  className={`text-left py-2 px-3 border text-xs transition-all ${currency === c ? "border-gold bg-[rgba(212,175,55,0.05)]" : "hairline hover:border-white/20"}`}
                >
                  <div className="uppercase tracking-widest text-muted-fg">{c}</div>
                  <div className={`font-mono text-sm mt-1 ${currency === c ? "text-gold" : "text-white"}`}>{formatMoney(balances[c] ?? 0, c)}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <section className="mt-16">
        <h2 className="font-display text-3xl tracking-tight">Top up · {currency.toUpperCase()}</h2>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mt-8">
          {PRESETS.map((p) => (
            <button
              key={p}
              data-testid={`preset-${p}`}
              type="button"
              onClick={() => setAmount(p.toString())}
              className={`py-4 border text-sm transition-all ${
                amount === p.toString()
                  ? "border-gold text-gold bg-[rgba(212,175,55,0.05)]"
                  : "hairline text-secondary-fg hover:text-white"
              }`}
            >
              {formatMoney(p, currency)}
            </button>
          ))}
        </div>

        <div className="mt-10 max-w-md">
          <label className="text-xs uppercase tracking-[0.25em] text-muted-fg">Custom amount</label>
          <input data-testid="topup-amount" type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="lux-input mt-2 font-display text-4xl" />
        </div>

        <button data-testid="topup-submit" onClick={topUp} disabled={loading} className="btn-gold inline-flex items-center gap-2 mt-8">
          <Plus size={14} /> {loading ? "Redirecting…" : `Top up ${amount ? formatMoney(parseFloat(amount) || 0, currency) : ""}`}
        </button>
      </section>
    </main>
  );
}
